import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TableWindow from '../../../../components/Table/TableWindow';
import useTablePages from '../../../../hooks/useTablePages';
import availableColumns from './WaterSuppliers.columns';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';

import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';

import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import FilterBarWaterSuppliers from './FilterBarWaterSuppliers';
import useMode from '../../../../hooks/useMode';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import WaterSupplierThirdPane from '../../../../components/WaterSupplier/WaterSupplierThirdPane';
import Button from '../../../../components/Button/Button';
import WaterSupplierEditModal from '../../../../components/WaterSupplier/WaterSupplierEditModal';
import { PortalWithState } from 'react-portal';
import { useHistory } from 'react-router-dom';
import {
  GET_WATER_SUPPLIER_IDS,
  GET_WATER_SUPPLIERS,
  WaterSupplierItem,
} from './WaterSuppliers.query';

type WaterSupplierDetailParams = {
  id?: string;
};

type WaterSupplierDetailProps = RouteComponentProps<WaterSupplierDetailParams>;

/**
 * @component Used for water suppliers page
 */
function WaterSuppliers({ match }: WaterSupplierDetailProps): JSX.Element {
  const history = useHistory();
  const mode = useMode();

  const [filters, setFilters] = useState({} as Record<string, unknown> | null);

  const { orderBy, onHeaderClick } = useOrderBy(
    'waterSuppliers',
    availableColumns,
    'profile.name'
  );

  const { loading, itemIds, selectedIds, setSelectedIds, itemLoader, refetch } =
    useTablePages<WaterSupplierItem, 'profileId'>(
      'waterSuppliers',
      GET_WATER_SUPPLIER_IDS,
      {
        where: filters,
        limit: null,
        orderBy,
      },
      GET_WATER_SUPPLIERS,
      (ids) => ({
        where: {
          profileId: ids,
        },
        limit: null,
        orderBy,
      }),
      'profileId'
    );

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(
      `/${useMode()}/water-suppliers`,
      itemIds,
      match.params.id,
      'profileId'
    );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'waterSuppliers');

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Water Suppliers">
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  ml={3}
                  variant="primary"
                  icon="plus"
                  onClick={openPortal}
                >
                  Add Water Supplier
                </Button>
                {portal(
                  <WaterSupplierEditModal
                    action="add"
                    onSave={(waterSupplier) => {
                      history.push(
                        `/${mode}/water-suppliers/${waterSupplier.profileId}`
                      );
                    }}
                    onClose={() => {
                      closePortal();
                      refetch();
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </PageBar>
        <FilterBarWaterSuppliers
          mb={4}
          filters={filters ?? {}}
          setFilters={setFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
          />
        )}
      </MainColumn>
      <WaterSupplierThirdPane
        profileId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

WaterSuppliers.propTypes = {
  match: PropTypes.any,
};

export default withRouter(WaterSuppliers);
